import { useEffect } from "react";
import { CONSENT_EVENT, readConsent, type CookieConsent } from "@/lib/cookie-consent";

/**
 * Instala o Meta Pixel de forma condicional ao consentimento de marketing.
 * - Só carrega o script depois que o usuário aceita "marketing".
 * - Só dispara PageView quando montado (por rota) e com consentimento ativo.
 * - Idempotente: usa flag global para não recarregar o snippet.
 */
export function MetaPixel({ pixelId }: { pixelId: string }) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const w = window as unknown as {
      fbq?: ((...args: unknown[]) => void) & {
        callMethod?: (...args: unknown[]) => void;
        queue?: unknown[];
        push?: unknown;
        loaded?: boolean;
        version?: string;
      };
      _fbq?: unknown;
      __kentroMetaPixelLoaded?: Record<string, boolean>;
    };

    const loadPixel = () => {
      w.__kentroMetaPixelLoaded = w.__kentroMetaPixelLoaded || {};
      if (!w.fbq) {
        // Snippet oficial do Meta Pixel.
        /* eslint-disable */
        (function (f: any, b: Document, e: string, v: string) {
          if (f.fbq) return;
          const n: any = (f.fbq = function () {
            n.callMethod
              ? n.callMethod.apply(n, arguments)
              : n.queue.push(arguments);
          });
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = "2.0";
          n.queue = [];
          const t = b.createElement(e) as HTMLScriptElement;
          t.async = true;
          t.src = v;
          const s = b.getElementsByTagName(e)[0];
          s.parentNode?.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        /* eslint-enable */
      }
      if (!w.__kentroMetaPixelLoaded[pixelId]) {
        w.fbq?.("init", pixelId);
        w.__kentroMetaPixelLoaded[pixelId] = true;
      }
      // Consent Mode do Pixel — coerente com o banner.
      const consent = readConsent();
      w.fbq?.("consent", consent?.marketing ? "grant" : "revoke");
      w.fbq?.("track", "PageView");
    };

    const consent = readConsent();
    if (consent?.marketing) loadPixel();

    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<CookieConsent>).detail;
      if (detail?.marketing) loadPixel();
    };
    window.addEventListener(CONSENT_EVENT, onChange as EventListener);
    return () => window.removeEventListener(CONSENT_EVENT, onChange as EventListener);
  }, [pixelId]);

  return (
    <noscript>
      <img
        height="1"
        width="1"
        style={{ display: "none" }}
        alt=""
        src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
      />
    </noscript>
  );
}

export const META_PIXEL_ID = "27166786012939155";

/** @deprecated usar META_PIXEL_ID — o pixel agora é único e global. */
export const CLINICAS_META_PIXEL_ID = META_PIXEL_ID;
